import { inviteTeaMemberCSVModalRoutingService } from 'components/InviteTeamMembersByCSVModal/InviteTeamMembersByCSVModal.ui.context';
import { ILocationFilterProps } from 'components/LocationFilterInput/LocationFilterInput.types';
import { SSFBarsProvider } from 'components/SSFBars/SSFBars.context';
import { RESEND_TEAM_MEMBERS_INVITES } from 'constants/endpoints';
import { useSSFBehaviors } from 'hooks';
import { teamMemberPageConfig } from 'pages/TeamMemberPage/TeamMemberPage.constants';
import { useAuthRouteDataContext } from 'providers/AuthRouteDataProvider/AuthRouteDataProvider.context';
import { createContext, FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { METHOD_POST } from 'requests';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { isUserAgencyAndAgencyLocationFilterForcedDisabledSelector } from 'store/selectors/featureFlagSelectors';
import isUserAgencySelector from 'store/selectors/userSelectors/isUserAgencySelector';
import { GQLAPIContextType, pageContextInitialState } from 'types/common.types';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';

export const TeamMemberPageContext = createContext<GQLAPIContextType>(
  pageContextInitialState,
) as GQLAPIContextType;

export const TeamMemberPageProvider: FC<{ locationId?: string }> = ({ children, locationId }) => {
  const { locationFilter } = useAuthRouteDataContext();
  const { apiRequest } = useAPIActions();
  const isUserAgencyAndAgencyLocationFilterForcedDisabled = useSelector(
    isUserAgencyAndAgencyLocationFilterForcedDisabledSelector,
  );

  const isUserAgency = useSelector(isUserAgencySelector);

  const [values, opts] = useSSFBehaviors(
    teamMemberPageConfig({
      location: locationFilter?.initialValue,
      isUserAgencyAndAgencyLocationFilterForcedDisabled,
    }),
  );
  const [showResendConfirmMessage, setShowResendConfirmMessage] = useState(false);

  const resendInviteTeamMembers = useCallback(
    () =>
      apiRequest(
        {
          __typename: 'LegacyAPI',
          method: METHOD_POST,
          url: RESEND_TEAM_MEMBERS_INVITES(values?.location?.id),
        },
        {
          onSuccess: () => {
            inviteTeaMemberCSVModalRoutingService.goToView('ReInviteTeamMemberSuccessFul');
          },
          onError: handleGenericErrorMessage,
        },
      ),
    [apiRequest, values?.location?.id],
  );

  const location = useMemo(
    () =>
      !!locationId && !Number.isNaN(locationId) && !isUserAgency
        ? (locationFilter?.data?.find(
            (locationOption) => Number(locationOption.value) === Number(locationId),
          ) as ILocationFilterProps['value'])
        : values.location,
    [isUserAgency, locationFilter?.data, locationId, values.location],
  );

  const value = {
    values: { ...values, location },
    resendInviteTeamMembers,
    setShowResendConfirmMessage,
    showResendConfirmMessage,
    ...opts,
  };
  return (
    <TeamMemberPageContext.Provider value={value}>
      <SSFBarsProvider>{children}</SSFBarsProvider>
    </TeamMemberPageContext.Provider>
  );
};
